'use client';

import { type MouseEvent } from 'react';
import { HStack } from '@terra/ui-styled-system';
import {
  BadgeHttp,
  MetaDefault,
  Tooltip,
  ArchivedLifecycleBadge,
} from '@terra/ui-design-system';
import { LinkSquare02Icon } from '@terra/ui-icons';

type SignalAssetLinkRowProps = {
  label: string;
  method?: string;
  canNavigate: boolean;
  tooltipTitle: string;
  onNavigate: () => void;
  onContextMenu: (event: MouseEvent<HTMLElement>) => void;
  stopPropagationOnClick?: boolean;
  isArchived?: boolean;
};

export function SignalAssetLinkRow({
  label,
  method,
  canNavigate,
  tooltipTitle,
  onNavigate,
  onContextMenu,
  stopPropagationOnClick,
  isArchived,
}: SignalAssetLinkRowProps) {
  return (
    <HStack
      gap="2"
      alignItems="center"
      p="2"
      bg="bg.subtleSoft"
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.default"
      cursor={canNavigate ? 'pointer' : 'default'}
      _hover={
        canNavigate
          ? { bg: 'bg.subtle', borderColor: 'border.muted' }
          : undefined
      }
      onClick={(event) => {
        if (!canNavigate) {
          return;
        }

        if (stopPropagationOnClick) {
          event.stopPropagation();
        }

        onNavigate();
      }}
      onContextMenu={(event) => {
        if (!canNavigate) {
          return;
        }

        if (stopPropagationOnClick) {
          event.stopPropagation();
        }

        onContextMenu(event);
      }}
      transition="all 0.15s"
    >
      <LinkSquare02Icon size={12} color="var(--colors-fg-muted)" />
      {method ? (
        <BadgeHttp method={method} size="sm">
          {method}
        </BadgeHttp>
      ) : null}
      <Tooltip title={tooltipTitle}>
        <MetaDefault
          color={canNavigate ? 'fg.default' : 'fg.muted'}
          truncate
          flex="1"
          _hover={canNavigate ? { textDecoration: 'underline' } : undefined}
        >
          {label}
        </MetaDefault>
      </Tooltip>
      {isArchived ? (
        <ArchivedLifecycleBadge lifecycle={{ state: 'ARCHIVED' }} />
      ) : null}
      {canNavigate ? <MetaDefault color="fg.muted">→</MetaDefault> : null}
    </HStack>
  );
}
